import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SalovareSelectField from '../common/SalovareSelectField';
import { FIELD_PLACEHOLDER_COLOR, fieldLabelSx } from '../common/formFieldStyles';
import { formatCurrency } from '../../utils/currencyUtils';
import { getLocalizedValue } from '../../constants/Languages';
import { availablePackages } from '../../utils/stockStatusUtils';
import { BRAND } from '../../theme/brandTokens';

const buildTextFieldSx = (hasError = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  minHeight: 48,
  '&:focus': {
    borderColor: hasError ? '#f44336' : BRAND.accent,
  },
  '&:disabled': {
    backgroundColor: '#f5f5f5',
    cursor: 'not-allowed',
  },
});

export const getProductLinesTotal = (products, lines) => (
  (lines || []).reduce((sum, line) => {
    const product = (products || []).find((item) => item.id === line.productId);
    const quantity = Number(line.quantity) || 0;
    return sum + ((Number(product?.price) || 0) * quantity);
  }, 0)
);

const ProductSaleLinesField = ({
  products = [],
  lines = [],
  onChange,
  readOnly = false,
  currencyCode,
  disableOutOfStock = false,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedProductId, setSelectedProductId] = useState('');
  const [quantity, setQuantity] = useState('1');

  const availableProducts = useMemo(() => products || [], [products]);
  const selectedProduct = availableProducts.find((product) => product.id === selectedProductId);
  const selectedAvailable = selectedProduct ? availablePackages(selectedProduct) : null;
  const parsedQuantity = Number.parseInt(quantity, 10);
  const exceedsAvailable = disableOutOfStock
    && selectedAvailable != null
    && Number.isFinite(parsedQuantity)
    && parsedQuantity > selectedAvailable;

  const handleAdd = () => {
    if (!selectedProductId || !Number.isFinite(parsedQuantity) || parsedQuantity < 1) {
      return;
    }

    if (disableOutOfStock && (selectedProduct?.isOutOfStock || exceedsAvailable)) {
      return;
    }

    const existing = (lines || []).find((line) => line.productId === selectedProductId);
    const nextLines = existing
      ? (lines || []).map((line) => (
        line.productId === selectedProductId
          ? { ...line, quantity: line.quantity + parsedQuantity }
          : line
      ))
      : [...(lines || []), { productId: selectedProductId, quantity: parsedQuantity }];

    onChange(nextLines);
    setSelectedProductId('');
    setQuantity('1');
  };

  const handleRemove = (productId) => {
    onChange((lines || []).filter((line) => line.productId !== productId));
  };

  const total = getProductLinesTotal(products, lines);

  return (
    <Box>
      {readOnly ? null : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 120px auto' },
            gap: 1.5,
            alignItems: 'end',
            mb: 2,
          }}
        >
          <SalovareSelectField
            label={t('productsManagement.sale.product')}
            value={selectedProductId}
            onChange={(event) => setSelectedProductId(event.target.value)}
            displayEmpty
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography component="span" sx={{ color: FIELD_PLACEHOLDER_COLOR }}>
                    {t('productsManagement.sale.product')}
                  </Typography>
                );
              }
              const product = availableProducts.find((item) => item.id === value);
              return getLocalizedValue(product?.name, i18n.language, undefined, '');
            }}
          >
            <MenuItem value="">
              <Typography sx={{ color: FIELD_PLACEHOLDER_COLOR }}>
                {t('productsManagement.sale.product')}
              </Typography>
            </MenuItem>
            {availableProducts.map((product) => {
              const outOfStock = Boolean(product.isOutOfStock) || availablePackages(product) === 0;
              return (
                <MenuItem key={product.id} value={product.id} disabled={disableOutOfStock && outOfStock}>
                  {getLocalizedValue(product.name, i18n.language, undefined, '')}
                  {disableOutOfStock && outOfStock ? ` (${t('productsManagement.card.outOfStock')})` : ''}
                </MenuItem>
              );
            })}
          </SalovareSelectField>

          <Box>
            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {t('productsManagement.sale.quantity')}
            </Typography>
            <Box
              component="input"
              type="number"
              min={1}
              aria-label={t('productsManagement.sale.quantity')}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            sx={buildTextFieldSx(exceedsAvailable)}
          />
          {exceedsAvailable ? (
            <Typography variant="caption" sx={{ color: 'warning.dark', mt: 0.5, display: 'block' }}>
              {t('productsManagement.sale.onlyNLeft', { count: selectedAvailable })}
            </Typography>
          ) : null}
        </Box>

          <Button
            type="button"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{ textTransform: 'none', minHeight: 48, borderColor: BRAND.line, color: BRAND.accentText }}
          >
            {t('productsManagement.sale.addLine')}
          </Button>
        </Box>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('productsManagement.sale.product')}</TableCell>
            <TableCell align="right">{t('productsManagement.sale.quantity')}</TableCell>
            <TableCell align="right">{t('productsManagement.sale.total')}</TableCell>
            {readOnly ? null : <TableCell align="right" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {(lines || []).length === 0 ? (
            <TableRow>
              <TableCell colSpan={readOnly ? 3 : 4}>
                <Typography variant="body2" color="text.secondary">
                  {t('productsManagement.sale.lines')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (lines || []).map((line) => {
            const product = (products || []).find((item) => item.id === line.productId);
            const lineTotal = (Number(product?.price) || 0) * (Number(line.quantity) || 0);
            return (
              <TableRow key={line.productId}>
                <TableCell>
                  {getLocalizedValue(product?.name, i18n.language, undefined, line.productId)}
                </TableCell>
                <TableCell align="right">{line.quantity}</TableCell>
                <TableCell align="right">{formatCurrency(lineTotal, currencyCode)}</TableCell>
                {readOnly ? null : (
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={t('productsManagement.actions.delete')}
                      onClick={() => handleRemove(line.productId)}
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1.5, textAlign: 'right' }}>
        {t('productsManagement.sale.total')}: {formatCurrency(total, currencyCode)}
      </Typography>
    </Box>
  );
};

export default ProductSaleLinesField;
